import React, { useRef } from 'react';
import {
  Paper,
  Grid,
  Divider,
  Box,
  ClickAwayListener,
  useTheme,
} from '@mui/material';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import KingBedOutlinedIcon from '@mui/icons-material/KingBedOutlined';

import StyledTextField from '@shared/components/StyledTextField';
import { useReferenceSearch } from './helper';

const ReferenceSearch = ({ lodgingSelection, setLodgingSelection }) => {
  const theme = useTheme();
  const inputRef = useRef(null);

  const {
    searchText,
    setSearchText,
    groupedOptions,
    loading,
    isOpen,
    setIsOpen,
    highlightMatch,
    resetSearch,
    handleSelect,
    isEditing,
    setIsEditing,
  } = useReferenceSearch({ setLodgingSelection });

  let displayValue = '';

  if (isEditing) {
    displayValue = searchText;
  } else if (lodgingSelection) {
    const { label, reference_city: referenceCity } = lodgingSelection.option;

    displayValue = referenceCity ? `${label} (${referenceCity.label})` : label;
  }

  let content;

  if (loading) {
    content = <Box sx={{ padding: 2 }}>Buscando ciudades y hoteles...</Box>;
  } else if (groupedOptions.length === 0) {
    content = <Box sx={{ padding: 2 }}>No se encontraron resultados</Box>;
  } else {
    content = groupedOptions.map(({ group, items }) => {
      const isCityGroup = group === 'city';

      const label = isCityGroup ? 'Ciudad' : 'Hotel';
      const Icon = isCityGroup ? RoomOutlinedIcon : KingBedOutlinedIcon;

      return (
        <Box key={group}>
          <Grid
            sx={{
              fontSize: '16px',
              padding: '16px 8px 0 8px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Grid container>
              <Grid
                item
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: { xs: '25px', sm: '30px' },
                }}
              >
                <Icon
                  sx={{
                    color: theme.tertiary,
                    fontWeight: 600,
                  }}
                />
              </Grid>

              <Grid item style={{ lineHeight: 1.5 }}>
                {label}
              </Grid>
            </Grid>

            <Divider
              orientation="vertical"
              sx={{
                margin: '8px 0 0',
                borderBottom: '1px solid gray',
                width: '100%',
                height: { xs: '2px', sm: 'auto' },
              }}
            />
          </Grid>

          {items.map(item => {
            const isSelected = lodgingSelection?.id === item.id;

            return (
              <Box
                key={item.option.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0,
                  p: '8px 24px',
                  cursor: 'pointer',
                  bgcolor: isSelected ? '#fff' : 'transparent',
                  transition: 'background-color 0.3s ease',
                  '&:hover': { bgcolor: '#f0f0f0' },
                }}
                onClick={() => handleSelect(item)}
              >
                <Box>
                  {highlightMatch(item.option.label, searchText).map(
                    ({ text, highlight, key }) =>
                      highlight ? (
                        <span
                          key={key}
                          style={{
                            color: theme.tertiary,
                            fontWeight: 600,
                            fontFamily: 'Open Sans,sans-serif',
                            display: 'contents',
                            lineHeight: 1.2,
                          }}
                        >
                          {text}
                        </span>
                      ) : (
                        <span key={key}>{text}</span>
                      )
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      );
    });
  }

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <StyledTextField
        inputRef={inputRef}
        fullWidth
        value={displayValue}
        onChange={e => {
          const value = e.target.value;

          setIsEditing(true);
          setSearchText(value);

          if (value === '') {
            setIsEditing(false);
            setLodgingSelection(null);
          }
        }}
        onBlur={() => {
          setIsEditing(false);
        }}
        onPaste={e => setSearchText(e.clipboardData.getData('text'))}
        placeholder="Ingresá una ciudad o un hotel"
        focusBorderPrimary
        showEndAdornment={!!displayValue}
        height="55px"
        minHeight="55px"
        onEndAdornmentClick={resetSearch}
      />

      {isOpen && (
        <ClickAwayListener onClickAway={() => setIsOpen(false)}>
          <Paper
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              width: '100%',
              maxHeight: 300,
              overflowY: 'auto',
              zIndex: 1300,
              backgroundColor: '#fff',
              fontFamily: 'Open Sans,sans-serif',
            }}
          >
            {content}
          </Paper>
        </ClickAwayListener>
      )}
    </Box>
  );
};

export default ReferenceSearch;
